<template>
  <div class="container">
    <div class="row">
      <div class="page-body">

        <!-- Error Messaging -->
        <div v-if="availResponseError">
          <div class="form-notification">
              <i class="fal fa-exclamation-circle"></i>
              <div class="message">
                  <h4>{{ $t('flightNoLongerAvailable') }}</h4>
                  <p>{{ $t('flightNoLongerAvailableParagraph') }} <a href="javascript:;" @click="$router.go(-1)" :title="$t('viewAllAvailableFlights')">{{ $t('viewAllAvailableFlights') }}</a>.</p>
              </div>
          </div>
        </div>

        <!-- Fail Messaging -->
        <div v-if="$route.query.error || bookPaymentFailed || bookFailed">
            <div class="form-notification error" v-if="$route.query.error == 'payment-failed' || bookPaymentFailed">
                <i class="fal fa-exclamation-circle"></i>
                <div class="message">
                    <h4>{{ $t('paymentError') }}</h4>
                    <p>{{ $t('paymentErrorParagraph') }}</p>
                </div>
            </div>

            <div class="form-notification error" v-if="bookFailed">
                <i class="fal fa-exclamation-circle"></i>
                <div class="message">
                    <h4>{{ $t('bookingFailed') }}</h4>
                    <p>{{ $t('bookingFailedErrorParagraph') }}</p>
                </div>
            </div>
        </div>

        <LoadingWidgetReasons v-if="!availResponse && !availResponseError || bookLoading" :title="$t('bookTheCheapestFlightsWithUs')" :reasons="widgetReasons" />

        <div class="book-body" v-show="availResponse && !bookLoading">
          <section class="book-col">
            <h1 v-if="currentStep === 0">{{ $t('flightBookStepOneLabel') }}</h1>
            <h1 v-if="currentStep === 1">{{ $t('flightBookStepTwoLabel') }}</h1>
            <h1 v-if="currentStep === 2">{{ $t('flightBookStepThreeLabel') }}</h1>

            <div v-if="currentStep !== 3 && availResponse && !bookLoading" class="content-panel mobile-charges-summary">
              <div class="panel-title toggle" @click="showMobileSummary = !showMobileSummary">
                <h3>{{ $t('summaryOfCharges') }}</h3>
                <div class="flight-details-toggle">
                  <span v-if="showMobileSummary"><i class="fal fa-minus"></i></span>
                  <span v-else><i class="fal fa-plus"></i></span> 
                </div>
              </div>
              <div class="panel-body" v-if="showMobileSummary">
                <h5>{{ $t('flightSummary') }}: <br />
                  {{ availResponse.offer.bounds[0].segments[0].from.cityName}}
                  <span class="airport-code">{{ availResponse.offer.bounds[0].segments[0].from.iataCode }}</span> to 

                  {{ availResponse.offer.bounds[0].segments[availResponse.offer.bounds[0].segments.length - 1].to.cityName }}
                  <span class="airport-code">{{ availResponse.offer.bounds[0].segments[availResponse.offer.bounds[0].segments.length - 1].to.iataCode }}</span>
                </h5>

                <div class="passenger-summary" v-if="availResponse.travelersSummary">
                  <div class="summary">
                    <div class="label">
                      {{ availResponse.travelersSummary.adults.count }} x 
                      <span v-if="availResponse.travelersSummary.adults.count > 1"> {{ $t('adults') }}</span>
                      <span v-else> {{ $t('adult') }}</span>
                    </div>

                    <div class="value">
                      {{ (availResponse.travelersSummary.adults.price.total - availResponse.travelersSummary.adults.price.tax) * availResponse.travelersSummary.adults.count | formatCurrency(availResponse.currency, 2) }}
                    </div>
                  </div>

                  <div class="summary" v-if="availResponse.travelersSummary.children.count > 0">
                    <div class="label">
                      {{ availResponse.travelersSummary.children.count }} x 
                      <span v-if="availResponse.travelersSummary.children.count > 1">{{ $t('children') }}</span>
                      <span v-else>{{ $t('child') }}</span>
                    </div>

                    <div class="value">
                      {{ (availResponse.travelersSummary.children.price.total - availResponse.travelersSummary.children.price.tax) * availResponse.travelersSummary.children.count | formatCurrency(availResponse.currency, 2) }}
                    </div>
                  </div>

                  <div class="summary" v-if="availResponse.travelersSummary.infants.count > 0">
                    <div class="label">
                      {{ availResponse.travelersSummary.infants.count }} x 
                      <span v-if="availResponse.travelersSummary.infants.count > 1">{{ $t('infants') }}</span>
                      <span v-else>{{ $t('infant') }}</span>
                    </div>

                    <div class="value">
                      {{ (availResponse.travelersSummary.infants.price.total - availResponse.travelersSummary.infants.price.tax) * availResponse.travelersSummary.infants.count | formatCurrency(availResponse.currency, 2) }}
                    </div>
                  </div>

                  <div v-if="ancillaryTotals && ancillaryTotals.length > 0" class="summary" v-for="upsell in ancillaryTotals" v-bind:key="upsell.name">
                    <div class="label">
                      {{upsell.count}} x {{ancillaryContentInfo[upsell.name].title}}
                    </div>

                    <div class="value">
                      {{upsell.sum | formatCurrency(availResponse.currency, 2)}}
                    </div>
                  </div>

                  <div class="summary">
                    <div class="label">
                      {{ $t('taxes') }}
                    </div>

                    <div class="value">
                      {{ availResponse.offer.price.tax | formatCurrency(availResponse.currency, 2) }}
                    </div>
                  </div>

                  <div class="summary total">
                    <div class="label">{{ $t('total') }}</div>
                    <div class="value">
                        {{ availResponse.offer.price.total + ancillaryTotalsTotal | formatCurrency(availResponse.currency, 2) }} {{availResponse.currency}}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FlightListingSummary v-if="availResponse && !bookLoading && currentStep !== 1 && currentStep !== 3" :flightDetails="availResponse" />

            <BookingPassengers v-if="availResponse && !bookLoading && currentStep === 0" :contactInfo='contactInfo' :travelers='availResponse.travelers' :travelerDataRequirements="availResponse.travelerDataRequirements" @nextStep="bookNextStep" />

            <BookingUpsells v-if="availResponse && !bookLoading && currentStep === 1" :currency="availResponse.currency" :passengers="bookData.prep.passengersInfo" :upsellData="bookData.prep.extraProducts" @nextStep="bookNextStep"  @previousStep="bookPreviousStep"/>

            <BookingPayment v-if="availResponse && !bookLoading && currentStep === 2" :currency="availResponse.currency" :bookingSummary="bookData.prep.summary" @previousStep="bookPreviousStep" @book="completeBooking"/>

            <div v-show="currentStep===3" class="book-security-iframe" id="iframeContainer">
              <iframe src="/payment-redirect.html" style="height: 350px; width: 100%;" id="paymentRedirectIFrame"></iframe>
            </div>

          </section>

          <aside class="summary-col" v-if="availResponse && !bookLoading">

            <div class="sticky">
              <section class="aside-panel charges-summary" v-if="availResponse">
                  <h4>{{ $t('summaryOfCharges') }}</h4>
                  <span>
                    <h5>{{ $t('flightSummary') }}: <br />
                      
                      {{ availResponse.offer.bounds[0].segments[0].from.iataCode | toCityName }}
                      <span class="airport-code">({{ availResponse.offer.bounds[0].segments[0].from.iataCode }})</span> to 

                      {{ availResponse.offer.bounds[0].segments[availResponse.offer.bounds[0].segments.length - 1].to.iataCode | toCityName }}
                      <span class="airport-code">({{ availResponse.offer.bounds[0].segments[availResponse.offer.bounds[0].segments.length - 1].to.iataCode }})</span>
                    </h5>

                    <div class="passenger-summary" v-if="availResponse.travelersSummary">
                      <div class="summary">
                        <div class="label">
                          {{ availResponse.travelersSummary.adults.count }} x 
                          <span v-if="availResponse.travelersSummary.adults.count > 1"> {{ $t('adults') }}</span>
                          <span v-else> {{ $t('adult') }}</span>
                        </div>
                        <div class="value">
                          {{(availResponse.travelersSummary.adults.price.total - availResponse.travelersSummary.adults.price.tax) * availResponse.travelersSummary.adults.count | formatCurrency(availResponse.currency, 2) }}
                        </div>
                      </div>
                      <div class="summary" v-if="availResponse.travelersSummary.children.count > 0">
                        <div class="label">
                          {{ availResponse.travelersSummary.children.count }} x 
                          <span v-if="availResponse.travelersSummary.children.count > 1">{{ $t('children') }}</span>
                          <span v-else>{{ $t('child') }}</span>
                        </div>
                        <div class="value">
                          {{(availResponse.travelersSummary.children.price.total - availResponse.travelersSummary.children.price.tax) * availResponse.travelersSummary.children.count | formatCurrency(availResponse.currency, 2) }}
                        </div>
                      </div>
                      <div class="summary" v-if="availResponse.travelersSummary.infants.count > 0">
                        <div class="label">
                          {{ availResponse.travelersSummary.infants.count }} x 
                          <span v-if="availResponse.travelersSummary.infants.count > 1">{{ $t('infants') }}</span>
                          <span v-else>{{ $t('infant') }}</span>
                        </div>
                        <div class="value">
                          {{(availResponse.travelersSummary.infants.price.total - availResponse.travelersSummary.infants.price.tax) * availResponse.travelersSummary.infants.count | formatCurrency(availResponse.currency, 2) }}
                        </div>
                      </div>

                      <div v-if="ancillaryTotals && ancillaryTotals.length > 0" class="summary" v-for="upsell in ancillaryTotals" v-bind:key="upsell.name">
                        <div class="label">
                          {{upsell.count}} x {{ancillaryContentInfo[upsell.name].title}}
                        </div>

                        <div class="value">
                          {{upsell.sum | formatCurrency(availResponse.currency, 2)}}
                        </div>
                      </div>

                      <div class="summary">
                        <div class="label">
                          {{ $t('taxes') }}
                        </div>

                        <div class="value">
                          {{ availResponse.offer.price.tax | formatCurrency(availResponse.currency, 2) }}
                        </div>
                      </div>

                      <div class="summary total">
                        <div class="label">{{ $t('total') }}</div>
                        <div class="value">
                            {{ availResponse.offer.price.total + ancillaryTotalsTotal | formatCurrency(availResponse.currency, 2) }} {{availResponse.currency}}
                        </div>
                      </div>
                    </div>
                  </span>
              </section>

              <section class="aside-panel reasons-to-book"> 
                <h4>{{ $t('includedWithYourBooking') }}:</h4>

                <ul class="checks">
                  <li>
                    <i class="fal fa-check"></i>
                    <span class="text">
                      <strong>{{ $t('theCheapestFlights') }}</strong>
                      {{ $t('cheapestFlightsParagraph') }}
                    </span>
                  </li>
                  <li>
                    <i class="fal fa-check"></i>
                    <span class="text">
                      <strong>{{ $t('excellentCustomerService') }}</strong>
                     {{ $t('customerServiceParagraph') }}
                    </span>
                  </li>
                  <li>
                    <i class="fal fa-check"></i>
                    <span class="text">
                      <strong>{{ $t('secureTransactions') }}</strong>
                      {{ $t('secureTransactionsParagraph') }}
                    </span>
                  </li>

                </ul>
              </section>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import BookingPassengers from '@/components/flights/FlightBookingPassengers';
import BookingPayment from '@/components/flights/FlightBookingPayment';
import BookingUpsells from '@/components/flights/FlightBookingUpsells';
import FlightListingSummary from '@/components/flights/FlightListingSummary';
import LoadingWidgetReasons from '@/components/LoadingWidgetReasons';

import { analytics } from "../../firebaseConfig";

import { mapGetters } from 'vuex';
import FlightsApi from "../../services/flightsApi";
import ancillaryContentInfo from '../../assets/data/ancillaryInfo.json';

export default {
  name: 'FlightBooking',
  data () {
    let self = this;
    return {
      api: {},
      title: 'Book Your Flights',
      bookSteps: [this.BookingPassengers, this.BookingPayment, this.BookingUpsells],
      currentStep: 0,
      ancillaryContentInfo: ancillaryContentInfo,
      ancillaryTotalsTotal: 0,
      ancillaryTotals: {},
      bookData: {
        contactInfo: {},
        passengers: [],
        anc: {},
        prep: {passengersInfo: {}, cartInfo: {}, extraProducts: {}, extraProductsResults: {}, summary: {}}
      },
      bookLoading: false,
      bookFailed: false,
      bookPaymentFailed: false,
      availResponse: null,
      contactInfo: { "emailAddress": "", "countryInfo": { "dialCode": 1, "code": "US" }, "phoneNumber": "" },
      availResponseError: null,
      showMobileSummary: false,
      widgetReasons: [
        {
          title: self.$t('theCheapestFlights'),
          description: self.$t('900AirlinesWorldwide'),
          icon: 'fal fa-plane'
        },
        {
          title: self.$t('customFlightItineraries'),
          description: self.$t('virtualInterlineFlightCombinations'),
          icon: 'fal fa-route'
        },
        {
          title: self.$t('247Support'),
          description: self.$t('emailChatOrCallAnytime'),
          icon: 'fal fa-user-headset'
        },
        {
          title: self.$t('secureTransactions'),
          description: self.$t('encryptedDataProtection'),
          icon: 'fal fa-lock-alt'
        }
      ],
    }
  },
  metaInfo() {
    let self = this;
    return {
      title: self.$t('flightBookStepOneLabel'),
    }
  },
  components: {
    BookingPassengers,
    BookingPayment,
    BookingUpsells,
    FlightListingSummary,
    LoadingWidgetReasons
  },
  
  watch: {
    'bookData.prep.extraProducts.availableExtraProducts': {
      handler: function (ancillaries) {
        let result = [];
        let sum = 0;
        if (ancillaries) {
          for(let i = 0; i < ancillaries.length; i++) {
            let total = this.calculateAncillarySelectionTotal(ancillaries[i]);
            if (total.count > 0) {
              sum += total.sum;
              result.push(total);
            }
          }
        }
        this.ancillaryTotalsTotal = sum;
        this.$set(this, 'ancillaryTotals', result);
      },
      deep: true
    }
  },
  mounted () {
    let self = this;
    let params = self.$route.params;
    this.api = new FlightsApi();

    self.registerWindowEvent();

    if (self.$route.query.error) {
      analytics.logEvent('paymentFailed', {agency: window.TravelPlatform.key, type: 'flight'});
    }

    self.availResponse = self.getAvailResponse(params.id);

    if (!self.availResponse) {
      self.api.availability(params.id).then((result) => {
        if (result.data.error) {
          self.availResponseError = result.data.error;
        }
        else {
          self.availResponse = result.data;

          if (self.$route.query.debug || window.TravelPlatform.key === 'localhost') {
            for(let i = 0; i < self.availResponse.travelers.length; i++) {
              let traveler =  self.availResponse.travelers[i];
              traveler.firstName = self.$moment(traveler.reference).format('dddd');
              traveler.lastName = self.$moment(traveler.reference).format('MMMM');
              traveler.gender = Math.random() > 0.5 ? 'MALE' : 'FEMALE';
              traveler.dob = traveler.type === 'adult'
                      ? self.$moment().subtract(30, 'years').format('YYYY-MM-DD')
                      : traveler.type === 'child'
                              ? self.$moment().subtract(10, 'years').format('YYYY-MM-DD')
                              : self.$moment().subtract(1, 'years').format('YYYY-MM-DD');
              if (self.availResponse.travelerDataRequirements) {
                if (self.availResponse.travelerDataRequirements.includes('PASSPORT_NUMBER')) {
                  traveler.passportNumber = `ABCDEF${i}`;
                }
                if (self.availResponse.travelerDataRequirements.includes('PASSPORT_ISSUING_COUNTRY')) {
                  traveler.nationality = `CA`;
                }
                if (self.availResponse.travelerDataRequirements.includes('PASSPORT_ISSUING_CITY')) {
                  traveler.passportIssuingCity = `Toronto`;
                }
                if (self.availResponse.travelerDataRequirements.includes('PASSPORT_ISSUING_DATE')) {
                  traveler.passportIssueDate = self.$moment().subtract(1, 'year').toDate();
                }
                if (self.availResponse.travelerDataRequirements.includes('PASSPORT_EXPIRY_DATE')) {
                  traveler.passportExpireDate = self.$moment().add(3, 'year').toDate();
                }
              }
            }

            self.contactInfo = { "emailAddress": "b2b.cardTokens.3d@etraveligroup.com", "countryInfo": { "dialCode": 1, "code": "US" }, "phoneNumber": "1234567897" };

          }

          self.$store.dispatch('updateAvailResponse', self.availResponse);

          analytics.logEvent('availability', {
            agency: window.TravelPlatform.key,
            type: 'flight',
            currency: self.availResponse.currency,
            origin: self.availResponse.offer.bounds[0].segments[0].from.iataCode,
            destination: (self.availResponse.offer.bounds.length > 1)
                ? self.availResponse.offer.bounds[1].segments[0].from.iataCode
                : self.availResponse.offer.bounds[0].segments[self.availResponse.offer.bounds[0].segments.length - 1].to.iataCode
          });
        }
      });
    }

  },
  computed: {
    ...mapGetters ([
      'getAvailResponse'
    ]),

  },
  methods: {
    calculateAncillarySelectionTotal(upsell) {
      let result = {name: upsell.productType, count: 0, sum: 0};
      let option = {};
      if (upsell.productRelation.type !== 'SEGMENT') {
        switch (upsell.productRelation.offer.type) {
          case "SIMPLE":
            if (upsell.selection > 0) {
              result.count = 1;
              result.sum = upsell.selection;
            }
            break;
          default:
            option = upsell.productRelation.offer.options[0];
            for(let i = 0; i < option.travelerReferences.length; i++) {
              if (option.travelerReferences[i].selection > 0) {
                result.sum += ( upsell.productType === "EXTRA.BAGGAGE"
                        ? option.price.total
                        : option.travelerReferences[i].selection );
                result.count++;
              }
            }
            break;
        }
      }
      return result;
    },
    bookNextStep: function(data) {
      let self = this;

      let passengers = [];
      switch (self.currentStep) {
        case 0:
          self.bookData.passengers = data.passengers;
          self.bookData.contactInfo = data.contactInfo;

          for(let i = 0; i < self.bookData.passengers.length; i++) {
            let passenger = self.bookData.passengers[i];
            let passengerToAdd = {
              travelerReference: passenger.travelerReference,
              firstName: passenger.firstName,
              lastName: passenger.lastName,
              gender: passenger.gender,
              dateOfBirth: passenger.dateOfBirth
            };
            if (i === 0) {
              passengerToAdd.email = passenger.email;
              passengerToAdd.phoneNumber = passenger.phoneNumber;
            }
            if (self.availResponse.travelerDataRequirements) {
              if (self.availResponse.travelerDataRequirements.includes('PASSPORT_ISSUING_COUNTRY')) {
                passengerToAdd.nationality = passenger.nationality;
              }
              if (self.availResponse.travelerDataRequirements.includes('PASSPORT_NUMBER')) {
                if (!passengerToAdd.travelDocument) passengerToAdd.travelDocument = {};
                passengerToAdd.travelDocument.passportNumber = passenger.passportNumber;
              }
              if (self.availResponse.travelerDataRequirements.includes('PASSPORT_ISSUING_CITY')) {
                passengerToAdd.travelDocument.issueCity = passenger.passportIssuingCity;
              }
              if (self.availResponse.travelerDataRequirements.includes('PASSPORT_ISSUING_DATE')) {
                passengerToAdd.travelDocument.issueDate = passenger.passportIssuingDate;
              }
              if (self.availResponse.travelerDataRequirements.includes('PASSPORT_EXPIRY_DATE')) {
                passengerToAdd.travelDocument.expiryDate = passenger.passportExpiryDate;
              }
            }
            passengers.push(passengerToAdd);
          }
          self.bookData.passengers = passengers;
          self.api.prepareForBook(self.availResponse.offerReference, passengers)
                  .then((result) => {
                    self.bookData.prep.cartInfo = result.data.cartInfo;
                    self.bookData.prep.extraProducts = result.data.extraProducts;

                    for(let index in result.data.cartInfo.travelers) {
                      let traveler = result.data.cartInfo.travelers[index];
                      let passenger = passengers.find(p => p.travelerReference === traveler.searchTravelerReference);
                      self.bookData.prep.passengersInfo[traveler.cartOrderTravelerReference] = passenger;
                    }

                    self.currentStep++;
                    analytics.logEvent('prepareForBook', {agency: window.TravelPlatform.key, type: 'flight', passengers: passengers.length});
                  });
          break;
        case 1:
          self.api.addAncillaries(self.bookData.prep.cartInfo.cartReference, data).then(
            (result) => {
              self.bookData.prep.extraProductsResults = result;
              analytics.logEvent('addAncillaries', {agency: window.TravelPlatform.key, type: 'flight'});
              self.api.bookingSummary(self.bookData.prep.cartInfo.cartReference)
                .then((summary) => {
                  self.bookData.prep.summary = summary.data;
                  self.currentStep++;
                  analytics.logEvent('readyForPayment', {agency: window.TravelPlatform.key, type: 'flight'});
                });
            });
          break;
      }
    },
    bookPreviousStep: function() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    registerWindowEvent: function () {
      let self = this;

      let eventMethod = window.addEventListener ? "addEventListener" : "attachEvent";
      let eventer = window[eventMethod];
      let messageEvent = eventMethod == "attachEvent" ? "onmessage" : "message";

      eventer(messageEvent,function(e) {
        let key = e.message ? "message" : "data";
        let data = e[key];
        //run function//
        if(data.messageId === 'bookContinue'){
          self.currentStep++;
          self.bookLoading = true;

          // Payment Iframe has already been completed, and now we're ready to resubmit the payment
          self.api.bookContinue(data.data.params.paymentReference, self.bookData.prep.paymentInfo.customer, data.data.params.amount, data.data.url)
              .then(function (result) {
                if (result.status === 200 && result.data.continueWithCardTokenResult && result.data.continueWithCardTokenResult.status === 'OK') {
                  self.$router.push(`/${self.$i18n.locale}/bookings/${result.data.continueWithCardTokenResult.orderReference}/${self.bookData.prep.paymentInfo.customer.email}`);
                  analytics.logEvent('bookingComplete', {agency: window.TravelPlatform.key, type: 'flight', amount: this.bookData.prep.summary.cart.cartAmount.total});
                }
                else {
                  self.bookLoading = false;
                  self.currentStep = 2;
                  self.bookPaymentFailed = true;
                }
              })
              .catch(function (err) {
                self.bookLoading = false;
                self.currentStep = 2;
                self.bookFailed = true;
                analytics.logEvent('paymentFailed', {agency: window.TravelPlatform.key, type: 'flight', stage: 'After3DS', error: err});
              });

        }
      },false);
    },
    completeBooking: function (paymentInfo) {
      let self = this;
      let cartReference = this.bookData.prep.summary.cart.cartReference;
      self.bookData.prep.paymentInfo = paymentInfo;

      self.bookLoading = true;
      self.bookFailed = false;
      self.bookPaymentFailed = false;

      self.api.book(cartReference, paymentInfo)
        .then(function (result) {
          result = result.data;

          if (result.status === 'PENDING' && result.pendingResponseReason === 'REQUIRED_3D_AUTHENTICATION') {
            self.bookLoading = false;
            let iframe = document.getElementById('paymentRedirectIFrame');
            // confirm that the iframe is sitting on payment-redirect.html page in case this is the second time around.
            iframe.contentWindow.location.href = '/payment-redirect.html';
            // delay the postMessage to allow the page to load.  It should load instantly as it was loaded previously.
            setTimeout(function () {
              iframe.contentWindow.postMessage(result.paymentRedirectData);
              self.bookData.prep.paymentResult = result;
              self.currentStep++;
            }, 300);
          }
          // TODO: Rob, Pass router prop isConfirmation
          else if (result.status === 'OK') {
            self.$router.push({
              path: `/bookings/${result.orderReference}/${self.bookData.prep.paymentInfo.customer.email}`, 
              query: { isConfirmation: true }
            });
            analytics.logEvent('bookingComplete', {agency: window.TravelPlatform.key, type: 'flight', amount: this.bookData.prep.summary.cart.cartAmount.total});
          }

          else {
            self.bookLoading = false;
            self.currentStep = 2;
            self.bookPaymentFailed = true;
          }
        })
        .catch(err => {
          self.bookLoading = false;
          self.currentStep = 2;
          self.bookFailed = true;
          analytics.logEvent('paymentFailed', {agency: window.TravelPlatform.key, type: 'flight', stage: 'Before3DS', error: err});
        });
      analytics.logEvent('paymentSubmitted', {agency: window.TravelPlatform.key, type: 'flight', amount: this.bookData.prep.summary.cart.cartAmount.total});
    },
  }
}
</script>
